import { useState, useMemo } from 'react'
import { initPayment } from '../utils/payments'
import { useAuthStore, usePlayerStore } from '../stores'
import { auth } from '../utils/backend'
import { apiClient } from '../utils/api'
import {
  Settings2, Lock, Mail, Phone, Shield, Trash2, Eye, EyeOff, CheckCircle2,
  AlertCircle, Loader2, Save, Key, UserX
} from 'lucide-react'

function PlayerSettings() {
  const { user, logout } = useAuthStore()
  const { players, updatePlayer } = usePlayerStore()

  const currentPlayer = useMemo(() => {
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [user, players])

  // Password state
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Email state
  const [newEmail, setNewEmail] = useState(user?.email || '')
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailMsg, setEmailMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Phone state
  const [newPhone, setNewPhone] = useState(currentPlayer?.phone || '')
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [phoneMsg, setPhoneMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Privacy state
  const [privacyLoading, setPrivacyLoading] = useState(false)

  // Delete Account state
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteMsg, setDeleteMsg] = useState<{ type: 'error'; text: string } | null>(null)

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Please fill in both fields' })
      return
    }
    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters' })
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Passwords do not match' })
      return
    }

    setPasswordLoading(true)
    setPasswordMsg(null)
    try {
      await auth.updateUserPassword(newPassword)
      setPasswordMsg({ type: 'success', text: 'Password updated successfully' })
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setPasswordMsg({ type: 'error', text: err.message || 'Failed to update password' })
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleChangeEmail = async () => {
    if (!newEmail || newEmail === user?.email) {
      setEmailMsg({ type: 'error', text: 'Please enter a different email' })
      return
    }

    setEmailLoading(true)
    setEmailMsg(null)
    try {
      const { backendClient } = await import('../utils/backend')
      const { error } = await backendClient.auth.updateUser({ email: newEmail })
      if (error) throw error
      setEmailMsg({ type: 'success', text: 'Confirmation sent to your new email' })
    } catch (err: any) {
      setEmailMsg({ type: 'error', text: err.message || 'Failed to update email' })
    } finally {
      setEmailLoading(false)
    }
  }

  const handleChangePhone = async () => {
    if (!currentPlayer?.id) return

    setPhoneLoading(true)
    setPhoneMsg(null)
    try {
      await updatePlayer(currentPlayer.id, { phone: newPhone })
      setPhoneMsg({ type: 'success', text: 'Phone number updated' })
    } catch (err: any) {
      setPhoneMsg({ type: 'error', text: err.message || 'Failed to update phone' })
    } finally {
      setPhoneLoading(false)
    }
  }

  const handleTogglePrivacy = async (field: string, currentValue: boolean) => {
    if (!currentPlayer?.id) return
    setPrivacyLoading(true)
    try {
      await updatePlayer(currentPlayer.id, { [field]: !currentValue })
    } catch (err: any) {
      console.error('Failed to update privacy:', err)
    } finally {
      setPrivacyLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      setDeleteMsg({ type: 'error', text: 'Type DELETE to confirm' })
      return
    }

    setDeleteLoading(true)
    setDeleteMsg(null)
    try {
      // Delete player record first
      if (currentPlayer?.id) {
        await apiClient.deletePlayer(currentPlayer.id)
      }
      // Sign out
      await logout()
    } catch (err: any) {
      setDeleteMsg({ type: 'error', text: err.message || 'Failed to delete account' })
      setDeleteLoading(false)
    }
  }

  // Subscription state
  const [subscription, setSubscription] = useState<any>(null)
  const [, setCheckingSub] = useState(false)

  const fetchSubscription = async () => {
    const { backendClient } = await import('../utils/backend')
    const { data: { session } } = await backendClient.auth.getSession()
    const accessToken = session?.access_token
    if (!accessToken) return
    setCheckingSub(true)
    try {
      // Force relative path so Vercel Serverless Functions are hit correctly
      const url = '/api/paystack/get-subscription'
      const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      if (res.ok) {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const json = await res.json()
          setSubscription(json.subscription)
        }
      }
    } catch (err) {
      console.error('Failed to fetch subscription', err)
    } finally {
      setCheckingSub(false)
    }
  }

  // Load subscription once
  useMemo(() => {
    void fetchSubscription()
  }, [user?.id])

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Upgrade banner */}
      {user && user.role === 'player' && (!subscription || subscription.status !== 'active') && (
        <div className="rounded-2xl p-4 bg-gradient-to-r from-yellow-400/10 to-yellow-200/5 border border-yellow-500/20 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-yellow-300">Upgrade to Player Pro</p>
            <p className="text-xs text-yellow-200">Unlock unlimited uploads, messaging, Trial Invitations, and Featured Player eligibility.</p>
          </div>
          <div>
            <button
              onClick={async () => {
                if (!user) return
                try {
                  const { authorization_url } = await initPayment('player_pro')
                  window.location.href = authorization_url
                } catch (err: any) {
                  alert(err.message || 'Failed to start payment')
                }
              }}
              className="px-4 py-2 rounded-xl bg-yellow-400 text-black font-bold"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Settings2 size={18} className="text-green-500" />
        <h3 className="text-xl font-bold text-white">Settings</h3>
      </div>

      {/* Change Password */}
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Lock size={18} className="text-yellow-400" />
          <h4 className="text-lg font-bold text-white">Change Password</h4>
        </div>

        {passwordMsg && (
          <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm ${
            passwordMsg.type === 'success'
              ? 'bg-green-500/10 border border-green-500/30 text-green-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            {passwordMsg.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {passwordMsg.text}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 block">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl text-white text-sm focus:border-green-500 outline-none transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 block">Confirm Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl text-white text-sm focus:border-green-500 outline-none transition"
            />
          </div>
          <button
            onClick={handleChangePassword}
            disabled={passwordLoading}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-500 rounded-xl text-white text-sm font-bold transition disabled:opacity-50"
          >
            {passwordLoading ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
            {passwordLoading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* Change Email */}
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Mail size={18} className="text-blue-400" />
          <h4 className="text-lg font-bold text-white">Change Email</h4>
        </div>

        {emailMsg && (
          <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm ${
            emailMsg.type === 'success'
              ? 'bg-green-500/10 border border-green-500/30 text-green-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            {emailMsg.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {emailMsg.text}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 block">Current Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl text-gray-500 text-sm cursor-not-allowed"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 block">New Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email"
              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl text-white text-sm focus:border-green-500 outline-none transition"
            />
          </div>
          <button
            onClick={handleChangeEmail}
            disabled={emailLoading}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-xl text-white text-sm font-bold transition disabled:opacity-50"
          >
            {emailLoading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {emailLoading ? 'Sending...' : 'Send Confirmation'}
          </button>
        </div>
      </div>

      {/* Change Phone */}
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Phone size={18} className="text-purple-400" />
          <h4 className="text-lg font-bold text-white">Phone Number</h4>
        </div>

        {phoneMsg && (
          <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm ${
            phoneMsg.type === 'success'
              ? 'bg-green-500/10 border border-green-500/30 text-green-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            {phoneMsg.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {phoneMsg.text}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 block">Phone Number</label>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+234 XXX XXX XXXX"
              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl text-white text-sm focus:border-green-500 outline-none transition"
            />
          </div>
          <button
            onClick={handleChangePhone}
            disabled={phoneLoading}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-xl text-white text-sm font-bold transition disabled:opacity-50"
          >
            {phoneLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {phoneLoading ? 'Saving...' : 'Save Phone Number'}
          </button>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Shield size={18} className="text-green-400" />
          <h4 className="text-lg font-bold text-white">Privacy</h4>
        </div>

        <div className="space-y-4">
          {[
            { field: 'discoverable_public', label: 'Public Profile', desc: 'Allow scouts and clubs to find your profile in search' },
            { field: 'hide_phone', label: 'Hide Phone Number', desc: 'Prevent your phone number from being visible to others' },
            { field: 'hide_email', label: 'Hide Email Address', desc: 'Prevent your email from being visible to others' },
            { field: 'is_public', label: 'Profile Visibility', desc: 'Make your full profile visible to scouts and clubs' },
          ].map((item) => {
            const currentValue = !!currentPlayer?.[item.field as keyof typeof currentPlayer]
            return (
              <div key={item.field} className="flex items-center justify-between p-3 bg-[#0d1117] rounded-xl border border-gray-800">
                <div>
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest">{item.desc}</p>
                </div>
                <button
                  onClick={() => handleTogglePrivacy(item.field, currentValue)}
                  disabled={privacyLoading}
                  className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${currentValue ? 'bg-green-500' : 'bg-gray-700'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${currentValue ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Delete Account */}
      <div className="bg-[#161b22] border border-red-500/20 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <UserX size={18} className="text-red-400" />
          <h4 className="text-lg font-bold text-red-400">Delete Account</h4>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
            <p className="text-sm text-red-300">
              This action is permanent and cannot be undone. All your data including profile, match history, messages, and documents will be permanently deleted.
            </p>
          </div>

          {deleteMsg && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              <AlertCircle size={16} />
              {deleteMsg.text}
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 block">
              Type <span className="text-red-400">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="w-full px-4 py-3 bg-[#0d1117] border border-red-500/30 rounded-xl text-white text-sm focus:border-red-500 outline-none transition"
            />
          </div>

          <button
            onClick={handleDeleteAccount}
            disabled={deleteLoading || deleteConfirm !== 'DELETE'}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-500 rounded-xl text-white text-sm font-bold transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {deleteLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {deleteLoading ? 'Deleting...' : 'Delete Account Permanently'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlayerSettings
